import React, { FC, useState, useEffect } from 'react';

import {
  Container,
  Table,
  TableRow,
  TableBody,
  TableHead,
  TableCell,
  TablePagination,
  Box,
} from '@material-ui/core';
import { isWidthUp, withWidth } from '@material-ui/core';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { Loader } from '../../../../components/Loader';
import { ProgressLine } from '../../../../components/UI/ProgressLine';
import { UsersProgress } from '../../const';
import useTableStyles from '../Table/styles';
import { TableViewHeader } from '../TableViewHeader';
import { TableViewHeaderProps } from '../TableViewHeader/TableViewHeader';
import useStyles from './styles';

interface UsersProgressTableProps extends TableViewHeaderProps {
  loading: boolean;
  total?: number | null;
  page: number;
  rowsPerPage: number;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  width: Breakpoint;
  data: UsersProgress[];
  handleExportData?: () => void;
  name?: string;
}

const UsersProgressTable: FC<UsersProgressTableProps> = ({
  title,
  description,
  goBack,
  page,
  rowsPerPage,
  handleChangeRowsPerPage,
  handleChangePage,
  width,
  loading,
  data,
  total = 0,
  handleExportData,
  name,
}) => {
  const tableClasses = useTableStyles();
  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = !isWidthUp('sm', width);

  const [isSorted, setIsSorted] = useState(false);
  const [isDesc, setIsDesc] = useState(false);
  const [sortedData, setSortedData] = useState<UsersProgress[]>([]);

  const isProgressSortAvailable = Boolean(total && rowsPerPage >= total);

  const handleSort = () => {
    if (isSorted) {
      if (isDesc) {
        setIsDesc(false);
      } else {
        setIsSorted(false);
      }
    } else {
      setIsSorted(true);
      setIsDesc(true);
    }
  };

  useEffect(() => {
    if (isSorted) {
      const tempData = [...data];
      setSortedData(
        tempData.sort((a, b) => (isDesc ? b.progress - a.progress : a.progress - b.progress)),
      );
    } else {
      setSortedData(data);
    }
  }, [data, isSorted, isDesc]);

  useEffect(() => {
    if (!isProgressSortAvailable) {
      setIsSorted(false);
      setIsDesc(false);
    }
  }, [isProgressSortAvailable]);

  return (
    <Container>
      <TableViewHeader
        title={title}
        description={description}
        goBack={goBack}
        handleExportData={handleExportData}
      />
      <div className={tableClasses.tableWrap}>
        <Table
          className={cx(tableClasses.tableInnerWrap, tableClasses.tableInnerWrapWithoutMargin)}
        >
          <TableHead>
            <TableRow className={tableClasses.tableHead}>
              <TableCell
                className={tableClasses.tableHeadCell}
                style={{
                  minWidth: 75,
                }}
              >
                {t('page.assignedLearnings.users')}
              </TableCell>
              {!isMobile && (
                <TableCell
                  className={tableClasses.tableHeadCell}
                  style={{
                    minWidth: 100,
                  }}
                >
                  {t('page.assignedLearnings.status')}
                </TableCell>
              )}
              <TableCell
                style={{
                  minWidth: isMobile ? 35 : 200,
                }}
                className={cx(
                  tableClasses.tableHeadCell,
                  isProgressSortAvailable && tableClasses.tableHeadSortable,
                )}
              >
                <Box
                  className={cx(isProgressSortAvailable && classes.boxHeading)}
                  onClick={handleSort}
                >
                  {t('page.assignedLearnings.progress')}
                  {isProgressSortAvailable && (
                    <ArrowDropDownIcon
                      className={cx(
                        isSorted && tableClasses.sorted,
                        isSorted && isDesc && tableClasses.sortedDesc,
                      )}
                      fontSize="small"
                    />
                  )}
                </Box>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody className={tableClasses.tableBody}>
            {loading ? (
              <TableRow className={cx(tableClasses.tableBodyCell, tableClasses.analyticsRow)}>
                <TableCell colSpan={isMobile ? 2 : 3} className={tableClasses.contentCell}>
                  <div className={classes.loaderWrapper}>
                    <Loader global={false} />
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map((item) => (
                <TableRow
                  data-testid={name + 'BodyRow'}
                  key={item?.id}
                  className={cx(tableClasses.tableBodyCell, tableClasses.analyticsRow)}
                >
                  <TableCell className={tableClasses.contentCell}>
                    {item?.userName}
                    {isMobile && item?.status && (
                      <div
                        className={cx(
                          tableClasses.statusText,
                          !!item?.completed && tableClasses.completed,
                        )}
                      >
                        {t(`common.${item.status}`)}
                      </div>
                    )}
                  </TableCell>
                  {!isMobile && (
                    <TableCell className={tableClasses.contentCell}>
                      {item.status && (
                        <div
                          className={cx(
                            tableClasses.statusText,
                            !!item?.completed && tableClasses.completed,
                          )}
                        >
                          {t(`common.${item.status}`)}
                        </div>
                      )}
                    </TableCell>
                  )}
                  <TableCell className={tableClasses.contentCell}>
                    <div className={tableClasses.progressCellWrapper}>
                      {!isMobile && (
                        <div className={tableClasses.progressWrapper}>
                          <ProgressLine
                            wrapperClass={tableClasses.progressLineWrapper}
                            lineClass={tableClasses.progressLine}
                            width={item.progress}
                          />
                        </div>
                      )}
                      <div
                        className={cx(
                          tableClasses.progressValueWrapper,
                          item.completed && tableClasses.completedProgress,
                        )}
                      >
                        {item.progress}%
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <div className={tableClasses.paginationWrap}>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={total || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            labelRowsPerPage={t('block.analyticsTable.paginationLabel')}
          />
        </div>
      </div>
    </Container>
  );
};

export default withWidth()(UsersProgressTable);
